/**
 * New Jersey State-Specific Diagnostic Questions
 * CORRECTED VERSION - All citations verified against actual N.J.A.C. 8:42
 * 
 * Regulatory Body: NJ Department of Health (DOH)
 * Primary Regulation: N.J.A.C. 8:42 - Licensing Standards for Home Health Agencies
 * CHHA Certification: N.J.A.C. 13:37-14 (Board of Nursing)
 * 
 * ACTUAL N.J.A.C. 8:42 STRUCTURE:
 * - Subchapter 1: General Provisions (8:42-1.1 to 1.2)
 * - Subchapter 2: Licensure Procedure (8:42-2.1 to 2.8)
 * - Subchapter 3: General Requirements (8:42-3.1 to 3.11)
 * - Subchapter 4: Governing Authority (8:42-4.1)
 * - Subchapter 5: Administration (8:42-5.1 to 5.3)
 * - Subchapter 6: Patient Care Services (8:42-6.1 to 6.7)
 * - Subchapter 7: Nursing Services (8:42-7.1 to 7.5) - INCLUDES CHHA
 * - Subchapter 8: Rehabilitation Services (8:42-8.1 to 8.3)
 * - Subchapter 9: Social Work Services (8:42-9.1 to 9.3) - NOT personnel/training
 * - Subchapter 10: Dietary Counseling Services (8:42-10.1 to 10.3)
 * - Subchapter 11: Medical/Health Records (8:42-11.1 to 11.2)
 * - Subchapter 12: Infection Prevention and Control (8:42-12.1 to 12.8)
 * - Subchapter 13: Patient Rights (8:42-13.1 to 13.2)
 * - Subchapter 14: Quality Assurance (8:42-14.1 to 14.2)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const NEW_JERSEY_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'NJ-A1',
    itemId: 'A1',
    stateCode: 'NJ',
    title: 'NJ DOH Home Health Agency License',
    description: 'New Jersey requires home health agencies to be licensed by DOH under N.J.A.C. 8:42',
    severity: 'high',
    regulationCode: '8:42-2.1',
    regulationName: 'Licensure',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/home-health-care/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from NJ DOH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'NJ DOH licenses are required under N.J.A.C. 8:42-2.1. New Jersey also requires a Certificate of Need (CN).',
      },
      {
        id: 'q2',
        question: 'What is your HHA license expiration date?',
        inputType: 'date',
        helpText: 'Found on your NJ DOH license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your NJ DOH Home Health Agency license is current and active per N.J.A.C. 8:42.',
      atRisk: 'Your HHA license expires within 120 days. Submit your renewal application to DOH before expiration.',
      nonCompliant: 'Operating without a valid DOH license violates N.J.A.C. 8:42 and can result in civil penalties. Contact NJ DOH at (800) 792-9770.',
      unknown: 'Verify your HHA license status at nj.gov/health or call NJ DOH at (800) 792-9770.',
    },
    stateNotes: [
      'New Jersey requires both a Certificate of Need (CN) and DOH license',
      'DOH conducts unannounced inspections',
      'Contact DOH Health Facilities at (800) 792-9770',
    ],
  },

  {
    id: 'NJ-A2',
    itemId: 'A2',
    stateCode: 'NJ',
    title: 'Medicare Certification Status',
    description: 'Medicare certification requirements for home health agencies',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoPs',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/Medicare/Provider-Enrollment-and-Certification/CertificationandComplianc/HHAs',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Medicare-covered home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - NJ Medicaid/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency currently Medicare certified?',
        inputType: 'select',
        options: [
          { value: 'certified', label: 'Yes, fully certified' },
          { value: 'pending', label: 'Certification pending' },
          { value: 'not_certified', label: 'Not Medicare certified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'certified', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_certified', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification is current or not required for your services.',
      atRisk: 'Medicare certification is pending. Ensure all Conditions of Participation (42 CFR 484) are met before survey.',
      nonCompliant: 'Providing Medicare services without certification violates federal requirements. Contact CMS at (800) 633-4227.',
      unknown: 'Verify Medicare certification status if providing Medicare-covered services.',
    },
    stateNotes: [
      'Medicare certification requires compliance with 42 CFR Part 484 Conditions of Participation',
      'NJ DOH conducts surveys on behalf of CMS for Medicare certification per N.J.A.C. 8:42-2.1',
      'Initial certification requires an unannounced survey by state agency',
      'Deemed status from accrediting organizations (JCAHO, CHAP, ACHC) satisfies survey requirements per N.J.S.A. 45:11-24',
    ],
  },

  {
    id: 'NJ-A3',
    itemId: 'A3',
    stateCode: 'NJ',
    title: 'NJ Medicaid Provider Enrollment',
    description: 'NJ FamilyCare (Medicaid) provider enrollment requirements',
    severity: 'high',
    regulationCode: '10:49',
    regulationName: 'Medicaid',
    agencyName: 'NJ Division of Medical Assistance and Health Services',
    agencyAbbr: 'DMAHS',
    agencyPhone: '(800) 701-0710',
    agencyUrl: 'https://www.state.nj.us/humanservices/dmahs/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide NJ Medicaid (NJ FamilyCare) funded services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicare/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency enrolled as an NJ Medicaid provider?',
        inputType: 'select',
        options: [
          { value: 'enrolled', label: 'Yes, currently enrolled' },
          { value: 'pending', label: 'Enrollment pending' },
          { value: 'not_enrolled', label: 'Not enrolled' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'enrolled', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_enrolled', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'NJ Medicaid provider enrollment is current.',
      atRisk: 'Medicaid enrollment is pending. Monitor status with DMAHS.',
      nonCompliant: 'Providing Medicaid services without enrollment violates NJ regulations. Contact DMAHS at (800) 701-0710.',
      unknown: 'Verify Medicaid enrollment status if providing NJ FamilyCare-funded services.',
    },
    stateNotes: [
      'NJ Medicaid enrollment requires active DOH license per N.J.A.C. 10:49',
      'Managed Care Organizations (MCOs) may have additional credentialing requirements',
      'Provider must maintain compliance with N.J.A.C. 8:42 to remain enrolled',
      'DMAHS conducts provider audits per N.J.S.A. 30:4D-6.1',
    ],
  },

  {
    id: 'NJ-A4',
    itemId: 'A4',
    stateCode: 'NJ',
    title: 'Administrator Appointment',
    description: 'New Jersey requires appointment of a qualified administrator per N.J.A.C. 8:42-5.1',
    severity: 'high',
    regulationCode: '8:42-5.1',
    regulationName: 'Administrator',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated administrator who meets NJ requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the administrator available during all hours of operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets N.J.A.C. 8:42-5.1 requirements.',
      atRisk: 'Administrator availability may not meet requirements. Review N.J.A.C. 8:42-5.2 for administrator responsibilities.',
      nonCompliant: 'No designated administrator. This violates N.J.A.C. 8:42-5.1. Designate a qualified administrator immediately.',
      unknown: 'Verify administrator qualifications against N.J.A.C. 8:42-5.1.',
    },
    stateNotes: [
      'Administrator must be appointed in writing by the governing authority per N.J.A.C. 8:42-5.1',
      'Administrator responsibilities defined in N.J.A.C. 8:42-5.2 include agency operations oversight',
      'Changes to administrator must be reported to DOH within 72 hours per N.J.A.C. 8:42-3.8',
      'Administrator must ensure compliance with N.J.S.A. 45:11-24 home health agency requirements',
    ],
  },

  {
    id: 'NJ-A5',
    itemId: 'A5',
    stateCode: 'NJ',
    title: 'Director of Nursing',
    description: 'New Jersey requires a director of nursing per N.J.A.C. 8:42-5.3',
    severity: 'high',
    regulationCode: '8:42-5.3',
    regulationName: 'DON',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated Director of Nursing (DON)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the DON a Registered Nurse licensed in New Jersey?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Director of Nursing meets N.J.A.C. 8:42-5.3 requirements.',
      atRisk: 'DON qualifications should be verified.',
      nonCompliant: 'No qualified Director of Nursing. N.J.A.C. 8:42-5.3 requires a licensed RN as DON.',
      unknown: 'Verify DON qualifications against N.J.A.C. 8:42-5.3.',
    },
    stateNotes: [
      'Changes to administrator or DON must be reported per N.J.A.C. 8:42-3.8',
      'Report to DOH at (800) 792-9770 or CNLapps@doh.nj.gov within 72 hours',
    ],
  },

  {
    id: 'NJ-A6',
    itemId: 'A6',
    stateCode: 'NJ',
    title: 'Patient Care Policies',
    description: 'Written policies governing patient care per N.J.A.C. 8:42-6.2',
    severity: 'medium',
    regulationCode: '8:42-6.2',
    regulationName: 'Care Policies',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Which patient care policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'admission', label: 'Admission and discharge criteria' },
          { value: 'care_plan', label: 'Plan of care development' },
          { value: 'supervision', label: 'Supervision of services' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'confidentiality', label: 'Patient confidentiality' },
          { value: 'transfer', label: 'Transfer and referral procedures' },
          { value: 'coordination', label: 'Coordination of services' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient care policies meet N.J.A.C. 8:42-6.2 requirements.',
      atRisk: 'Patient care policies are incomplete. Review N.J.A.C. 8:42-6.2 for required elements.',
      nonCompliant: 'Critical patient care policies are missing. This violates N.J.A.C. 8:42-6.2.',
      unknown: 'Review patient care policies against N.J.A.C. 8:42-6.2.',
    },
    stateNotes: [
      'Written policies must be reviewed at least annually per N.J.A.C. 8:42-6.2',
      'Policies must address patient care coordination per N.J.A.C. 8:42-6.1',
      'Complaint procedures must include DOH hotline (800) 792-9770 per N.J.S.A. 45:11-24',
      'Emergency procedures must be established per N.J.A.C. 8:42-3.10',
    ],
  },

  // ============================================
  // SECTION B: CHHA CERTIFICATION & TRAINING
  // ============================================
  {
    id: 'NJ-B1',
    itemId: 'B1',
    stateCode: 'NJ',
    title: 'CHHA Certification Requirements',
    description: 'All homemaker-home health aides must be certified by the NJ Board of Nursing per N.J.A.C. 13:37-14',
    severity: 'high',
    regulationCode: '8:42-7.5',
    regulationName: 'CHHA Certification',
    agencyName: 'New Jersey Board of Nursing',
    agencyAbbr: 'NJBON',
    agencyPhone: '(973) 504-6430',
    agencyUrl: 'https://www.njconsumeraffairs.gov/nur/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your homemaker-home health aides hold valid CHHA certification from the NJ Board of Nursing?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides are certified CHHAs' },
          { value: 'most', label: 'Most are certified, some are pending' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'N.J.A.C. 8:42-7.5 requires CHHAs to be certified by the Board of Nursing per N.J.A.C. 13:37-14',
      },
      {
        id: 'q2',
        question: 'Do you verify CHHA certification status before assigning aides to patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always verify first' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All CHHAs hold valid Board of Nursing certification per N.J.A.C. 8:42-7.5 and N.J.A.C. 13:37-14.',
      atRisk: 'Some CHHAs may be missing certification. N.J.A.C. 8:42-7.5 requires Board of Nursing certification before patient contact.',
      nonCompliant: 'Using uncertified aides violates N.J.A.C. 8:42-7.5. All CHHAs must be certified by the Board of Nursing.',
      unknown: 'Verify CHHA certification status with the NJ Board of Nursing.',
    },
    stateNotes: [
      'CHHA certification is issued by NJ Board of Nursing, not DOH',
      'Training requires 76 hours (60 classroom + 16 clinical) per N.J.A.C. 13:37-14.4',
      'Verify certifications at njconsumeraffairs.gov',
    ],
  },

  {
    id: 'NJ-B2',
    itemId: 'B2',
    stateCode: 'NJ',
    title: 'CHHA Training Program Requirements',
    description: 'NJ requires 76 hours of training for CHHA certification per N.J.A.C. 13:37-14.4',
    severity: 'high',
    regulationCode: '13:37-14.4',
    regulationName: 'CHHA Training',
    agencyName: 'New Jersey Board of Nursing',
    agencyAbbr: 'NJBON',
    agencyPhone: '(973) 504-6430',
    agencyUrl: 'https://www.njconsumeraffairs.gov/nur/',
    questions: [
      {
        id: 'q1',
        question: 'Did all your CHHAs complete a Board of Nursing approved training program with the following components?',
        inputType: 'checklist',
        options: [
          { value: '60_classroom', label: '60 hours of classroom instruction' },
          { value: '16_clinical', label: '16 hours of clinical instruction' },
          { value: 'competency', label: 'Competency evaluation' },
          { value: 'approved_program', label: 'Board of Nursing approved program' },
          { value: 'documentation', label: 'Training documentation on file' },
        ],
        required: true,
        helpText: 'N.J.A.C. 13:37-14.4 requires 76 total hours (60 classroom + 16 clinical)',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CHHA training meets N.J.A.C. 13:37-14.4 requirements (76 hours with competency evaluation).',
      atRisk: 'Training documentation may be incomplete. Verify all CHHAs completed Board-approved 76-hour program.',
      nonCompliant: 'CHHA training does not meet NJ requirements. N.J.A.C. 13:37-14.4 requires 76 hours from an approved program.',
      unknown: 'Review CHHA training against N.J.A.C. 13:37-14.4 requirements.',
    },
    stateNotes: [
      'Training must be from a Board of Nursing approved program per N.J.A.C. 13:37-14.4',
      '60 hours classroom instruction required per N.J.A.C. 13:37-14.4(a)',
      '16 hours supervised clinical instruction required per N.J.A.C. 13:37-14.4(b)',
      'Competency evaluation must be documented per N.J.S.A. 45:11-24.2',
    ],
  },

  {
    id: 'NJ-B3',
    itemId: 'B3',
    stateCode: 'NJ',
    title: 'RN Supervision of CHHAs',
    description: 'CHHAs must work under RN supervision per N.J.A.C. 8:42-7.5 and N.J.A.C. 13:37-6.2',
    severity: 'high',
    regulationCode: '8:42-7.5',
    regulationName: 'CHHA Supervision',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Does an RN orient each CHHA to each patient before services begin?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
        helpText: 'N.J.A.C. 8:42-7.5(a)2.i requires RN orientation before CHHA renders services',
      },
      {
        id: 'q2',
        question: 'Does the RN provide written instructions to the CHHA for each patient?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'How often does an RN conduct supervisory visits for each CHHA?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'Every 14 days or more frequently' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'every_60', label: 'Every 60 days' },
          { value: 'rarely', label: 'Rarely / no schedule' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'every_14' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q3', value: 'every_30' },
      ], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q3', value: ['every_60', 'rarely'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision of CHHAs meets N.J.A.C. 8:42-7.5 and N.J.A.C. 13:37-6.2 requirements.',
      atRisk: 'RN supervision may not meet NJ requirements. Ensure RN orientation, written instructions, and regular supervision.',
      nonCompliant: 'CHHA supervision is inadequate. N.J.A.C. 8:42-7.5 requires RN orientation and supervision per N.J.A.C. 13:37-6.2.',
      unknown: 'Review CHHA supervision practices against N.J.A.C. 8:42-7.5.',
    },
    stateNotes: [
      'RN must orient CHHA to patient before services begin per N.J.A.C. 8:42-7.5(a)2.i',
      'Written instructions must be provided and kept in patient home per N.J.A.C. 8:42-7.5(a)2.i',
      'Supervisory visits required at least every 14 days per N.J.A.C. 13:37-6.2',
      'RN must be available for consultation during CHHA service hours per N.J.S.A. 45:11-24',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND CHECKS
  // ============================================
  {
    id: 'NJ-C1',
    itemId: 'C1',
    stateCode: 'NJ',
    title: 'Criminal Background Check Requirements',
    description: 'NJ requires background checks for CHHA certification per N.J.S.A. 45:11-24.3',
    severity: 'high',
    regulationCode: '45:11-24.3',
    regulationName: 'Background Checks',
    agencyName: 'New Jersey Board of Nursing',
    agencyAbbr: 'NJBON',
    agencyPhone: '(973) 504-6430',
    agencyUrl: 'https://www.njconsumeraffairs.gov/nur/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL CHHAs, which background checks are on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'state_cbc', label: 'NJ State Police criminal history check' },
          { value: 'fbi', label: 'FBI fingerprint-based background check' },
          { value: 'certification', label: 'Board of Nursing certification (includes background check)' },
          { value: 'sex_offender', label: 'Sex Offender Registry check' },
          { value: 'oig', label: 'OIG/Medicaid exclusion list verification' },
        ],
        required: true,
        helpText: 'N.J.S.A. 45:11-24.3 requires fingerprint-based state and FBI checks for CHHA certification',
      },
      {
        id: 'q2',
        question: 'Do CHHAs begin working with patients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete per N.J.S.A. 45:11-24.3.',
      atRisk: 'Background check documentation may be incomplete.',
      nonCompliant: 'CHHAs working before background check clearance or without proper documentation violates N.J.S.A. 45:11-24.3.',
      unknown: 'Audit all CHHA files for background check documentation per N.J.S.A. 45:11-24.3.',
    },
    stateNotes: [
      'Background checks are part of CHHA certification process through Board of Nursing',
      'Both state and FBI fingerprint checks required',
      'Disqualifying crimes prevent certification',
    ],
  },

  {
    id: 'NJ-C2',
    itemId: 'C2',
    stateCode: 'NJ',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal requirement to check exclusion lists for Medicare/Medicaid providers',
    severity: 'high',
    regulationCode: '42 U.S.C. 1320a-7',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG List of Excluded Individuals/Entities (LEIE) for all employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly' },
          { value: 'quarterly', label: 'Yes, quarterly' },
          { value: 'hire_only', label: 'Only at hire' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document all exclusion list checks?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, documented in personnel files' },
          { value: 'no', label: 'No documentation' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['monthly', 'quarterly'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification meets federal requirements.',
      atRisk: 'Exclusion list checks should be performed monthly. Employing excluded individuals can result in civil monetary penalties.',
      nonCompliant: 'Not checking OIG exclusion lists violates federal requirements. Penalties can exceed $10,000 per violation.',
      unknown: 'Review OIG exclusion list requirements at exclusions.oig.hhs.gov.',
    },
    stateNotes: [
      'OIG LEIE checks required for Medicare/Medicaid participation per 42 U.S.C. 1320a-7',
      'SAM.gov exclusion database must also be checked per N.J.A.C. 10:49',
      'NJ Medicaid requires exclusion verification per N.J.S.A. 30:4D-17',
      'Document all exclusion list checks with date and results',
    ],
  },

  // ============================================
  // SECTION D: MEDICAL/HEALTH RECORDS
  // ============================================
  {
    id: 'NJ-D1',
    itemId: 'D1',
    stateCode: 'NJ',
    title: 'Medical/Health Records Policies',
    description: 'Policies and procedures for medical records per N.J.A.C. 8:42-11.1',
    severity: 'high',
    regulationCode: '8:42-11.1',
    regulationName: 'Records',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do your patient records contain the following required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'demographics', label: 'Patient demographics and emergency contacts' },
          { value: 'physician_orders', label: 'Physician orders/certification' },
          { value: 'assessment', label: 'Initial assessment' },
          { value: 'care_plan', label: 'Plan of care with goals' },
          { value: 'visit_notes', label: 'Visit notes for all services' },
          { value: 'supervision', label: 'RN supervision notes' },
          { value: 'consents', label: 'Signed consents and authorizations' },
          { value: 'discharge', label: 'Discharge summary (when applicable)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient records contain required elements per N.J.A.C. 8:42-11.1.',
      atRisk: 'Patient records may be missing required elements. Review N.J.A.C. 8:42-11.1 and 11.2.',
      nonCompliant: 'Patient records are incomplete. This violates N.J.A.C. 8:42-11.1.',
      unknown: 'Review patient record requirements under N.J.A.C. 8:42-11.',
    },
    stateNotes: [
      'Records must be maintained per N.J.A.C. 8:42-11.1 policies and procedures',
      'Records must be retained for at least 7 years per N.J.S.A. 45:11-24',
      'Patient records must be confidential and secure per N.J.A.C. 8:42-11.2',
      'Records available for DOH inspection per N.J.A.C. 8:42-2.4',
    ],
  },

  {
    id: 'NJ-D2',
    itemId: 'D2',
    stateCode: 'NJ',
    title: 'Medical Records Content',
    description: 'Required content for medical/health records per N.J.A.C. 8:42-11.2',
    severity: 'high',
    regulationCode: '8:42-11.2',
    regulationName: 'Record Content',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do your CHHA visit notes include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'date_time', label: 'Date and time of visit' },
          { value: 'services', label: 'Services provided' },
          { value: 'patient_status', label: 'Patient status/observations' },
          { value: 'care_plan', label: 'Adherence to care plan/written instructions' },
          { value: 'signature', label: 'CHHA signature' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are copies of RN written instructions kept in the patient\'s home?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'N.J.A.C. 8:42-7.5(a)2.i requires copies in the patient\'s home',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Visit documentation meets N.J.A.C. 8:42-11.2 and 8:42-7.5 requirements.',
      atRisk: 'Visit documentation may be incomplete. Review N.J.A.C. 8:42-11.2 requirements.',
      nonCompliant: 'Visit documentation is inadequate. This violates N.J.A.C. 8:42-11.2.',
      unknown: 'Review documentation requirements under N.J.A.C. 8:42-11.2.',
    },
    stateNotes: [
      'Visit notes must document services provided per N.J.A.C. 8:42-11.2',
      'CHHA must sign visit documentation per N.J.A.C. 8:42-7.5',
      'RN written instructions must be in patient home per N.J.A.C. 8:42-7.5(a)2.i',
      'Documentation must reflect adherence to plan of care per N.J.S.A. 45:11-24',
    ],
  },

  // ============================================
  // SECTION E: INFECTION PREVENTION AND CONTROL
  // ============================================
  {
    id: 'NJ-E1',
    itemId: 'E1',
    stateCode: 'NJ',
    title: 'Infection Prevention and Control Program',
    description: 'New Jersey requires an infection prevention and control program per N.J.A.C. 8:42-12.1',
    severity: 'high',
    regulationCode: '8:42-12.1',
    regulationName: 'Infection Control',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written infection prevention and control program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your infection control program include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'ppe', label: 'Personal protective equipment guidelines' },
          { value: 'bloodborne', label: 'Bloodborne pathogen exposure procedures' },
          { value: 'outbreak', label: 'Disease outbreak response plan' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection prevention and control program meets N.J.A.C. 8:42-12.1 requirements.',
      atRisk: 'Infection control program may be incomplete. Review N.J.A.C. 8:42-12.1 through 12.8.',
      nonCompliant: 'No infection prevention and control program. This violates N.J.A.C. 8:42-12.1.',
      unknown: 'Review infection control requirements under N.J.A.C. 8:42-12.',
    },
    stateNotes: [
      'Written infection control program required per N.J.A.C. 8:42-12.1',
      'Program must address bloodborne pathogen exposure per N.J.A.C. 8:42-12.6',
      'Staff training on infection control required per N.J.A.C. 8:42-12.8',
      'Surveillance and reporting requirements per N.J.S.A. 26:4-15 (communicable diseases)',
    ],
  },

  {
    id: 'NJ-E2',
    itemId: 'E2',
    stateCode: 'NJ',
    title: 'TB Testing Requirements',
    description: 'Tuberculosis testing for home health agency personnel',
    severity: 'high',
    regulationCode: '8:42-12.7',
    regulationName: 'TB Testing',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your CHHAs have current TB screening results on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have current TB screening' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'N.J.A.C. 8:42-12.7 requires TB testing per CDC recommendations',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all CHHAs per N.J.A.C. 8:42-12.7.',
      atRisk: 'Some TB screenings may be expired. Review all CHHA files.',
      nonCompliant: 'CHHAs are working without valid TB screening. This violates N.J.A.C. 8:42-12.7.',
      unknown: 'Verify TB screening status for all CHHAs per N.J.A.C. 8:42-12.7.',
    },
    stateNotes: [
      '2023 amendments updated TB testing to align with CDC recommendations',
      'Risk assessment approach may be used per CDC guidelines',
    ],
  },

  // ============================================
  // SECTION F: PATIENT RIGHTS
  // ============================================
  {
    id: 'NJ-F1',
    itemId: 'F1',
    stateCode: 'NJ',
    title: 'Patient Rights Notice',
    description: 'Written notice of patient rights per N.J.A.C. 8:42-13.1',
    severity: 'high',
    regulationCode: '8:42-13.1',
    regulationName: 'Rights',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written notice of patient rights before initiating care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'N.J.A.C. 8:42-13.2(a)2 requires written notice prior to initiation of care',
      },
      {
        id: 'q2',
        question: 'Does your patient rights notice include the DOH complaint hotline?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - includes 1-800-792-9770' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'N.J.A.C. 8:42-13.2(a)21 requires the complaint hotline number',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notice meets N.J.A.C. 8:42-13.1 and 13.2 requirements.',
      atRisk: 'Patient rights notice may be incomplete. Ensure DOH complaint hotline is included.',
      nonCompliant: 'No patient rights notice provided. This violates N.J.A.C. 8:42-13.1.',
      unknown: 'Review patient rights requirements under N.J.A.C. 8:42-13.',
    },
    stateNotes: [
      'Written notice required before care initiation per N.J.A.C. 8:42-13.2(a)2',
      'DOH complaint hotline (800) 792-9770 must be included per N.J.A.C. 8:42-13.2(a)21',
      'Patient rights enumerated in N.J.A.C. 8:42-13.2(a)1-21',
      'Patients may file complaints with DOH per N.J.S.A. 45:11-24',
    ],
  },

  // ============================================
  // SECTION G: QUALITY ASSURANCE
  // ============================================
  {
    id: 'NJ-G1',
    itemId: 'G1',
    stateCode: 'NJ',
    title: 'Quality Assurance Program',
    description: 'New Jersey requires a quality assurance program per N.J.A.C. 8:42-14.1',
    severity: 'medium',
    regulationCode: '8:42-14.1',
    regulationName: 'QA Program',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a quality assurance program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which quality metrics does your program track? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_outcomes', label: 'Patient health outcomes' },
          { value: 'satisfaction', label: 'Patient satisfaction' },
          { value: 'complaints', label: 'Complaint tracking and resolution' },
          { value: 'incidents', label: 'Incident reports and trends' },
          { value: 'staff_competency', label: 'Staff competency evaluations' },
          { value: 'documentation', label: 'Documentation audits' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance program meets N.J.A.C. 8:42-14.1 requirements.',
      atRisk: 'Quality assurance program may need enhancement. Review N.J.A.C. 8:42-14.2.',
      nonCompliant: 'No quality assurance program. This violates N.J.A.C. 8:42-14.1.',
      unknown: 'Review QA requirements under N.J.A.C. 8:42-14.',
    },
    stateNotes: [
      'QA program required per N.J.A.C. 8:42-14.1',
      'Program must evaluate patient care services per N.J.A.C. 8:42-14.2',
      'Quality review includes incident tracking and resolution per N.J.S.A. 45:11-24',
      'Documentation of QA activities required for DOH inspection',
    ],
  },

  // ============================================
  // SECTION H: ABUSE REPORTING
  // ============================================
  {
    id: 'NJ-H1',
    itemId: 'H1',
    stateCode: 'NJ',
    title: 'Mandated Reporter Training',
    description: 'All healthcare professionals are mandated reporters per N.J.S.A. 52:27D-409',
    severity: 'high',
    regulationCode: '52:27D-409',
    regulationName: 'Abuse Reporting',
    agencyName: 'NJ Division of Aging Services - Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '(877) 222-3737',
    agencyUrl: 'https://www.nj.gov/humanservices/doas/services/aps/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff receive training on mandated reporter responsibilities?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained' },
          { value: 'most', label: 'Most staff are trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected abuse to Adult Protective Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - know to call county APS or (877) 222-3737' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on mandated reporter duties per N.J.S.A. 52:27D-409.',
      atRisk: 'Some staff may lack mandated reporter training. Complete training for all staff.',
      nonCompliant: 'Staff are not trained on mandated reporter duties. This violates N.J.S.A. 52:27D-409.',
      unknown: 'Verify all staff have received mandated reporter training.',
    },
    stateNotes: [
      'Reports made to county Adult Protective Services provider',
      'Statewide APS hotline: (877) 222-3737',
      'Healthcare professionals are mandated reporters under 2010 amendment',
      'Good faith reporters are protected from liability',
    ],
  },

  // ============================================
  // SECTION I: IDENTIFICATION & CONSUMER GUIDE
  // ============================================
  {
    id: 'NJ-I1',
    itemId: 'I1',
    stateCode: 'NJ',
    title: 'CHHA Identification Tags',
    description: 'CHHAs must wear identification tags per N.J.A.C. 8:42-3.1',
    severity: 'medium',
    regulationCode: '8:42-3.1',
    regulationName: 'Identification',
    agencyName: 'New Jersey Department of Health',
    agencyAbbr: 'NJ DOH',
    agencyPhone: '(800) 792-9770',
    agencyUrl: 'https://www.nj.gov/health/healthfacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do all CHHAs wear identification tags with their photograph while providing care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'N.J.A.C. 8:42-3.1 requires photo ID tags worn in an easily visible place',
      },
      {
        id: 'q2',
        question: 'Do you provide patients with the Board of Nursing consumer guide for CHHAs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'N.J.A.C. 8:42-3.1 requires providing the consumer guide',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CHHA identification and consumer guide requirements are met per N.J.A.C. 8:42-3.1.',
      atRisk: 'Consumer guide may not be provided. Obtain from njconsumeraffairs.gov.',
      nonCompliant: 'CHHAs are not wearing required identification tags. This violates N.J.A.C. 8:42-3.1.',
      unknown: 'Review identification requirements under N.J.A.C. 8:42-3.1.',
    },
    stateNotes: [
      'Photo ID tags required per N.J.A.C. 8:42-3.1',
      'ID must be worn in easily visible location during patient care',
      'Consumer guide for CHHAs must be provided per N.J.A.C. 8:42-3.1',
      'Guide available from Board of Nursing per N.J.S.A. 45:11-24.3',
    ],
  },

  // ============================================
  // SECTION J: EVV FOR MEDICAID
  // ============================================
  {
    id: 'NJ-J1',
    itemId: 'J1',
    stateCode: 'NJ',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid-funded personal care services per 21st Century Cures Act',
    severity: 'high',
    regulationCode: '21st Century Cures Act',
    regulationName: 'EVV',
    agencyName: 'NJ Division of Medical Assistance and Health Services',
    agencyAbbr: 'DMAHS',
    agencyPhone: '(800) 701-0710',
    agencyUrl: 'https://www.state.nj.us/humanservices/dmahs/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide NJ Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicare/private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use an NJ DMAHS-approved EVV system?',
        inputType: 'select',
        options: [
          { value: 'sandata', label: 'Yes - Sandata (state system)' },
          { value: 'alternate', label: 'Yes - Approved alternate vendor' },
          { value: 'partial', label: 'Partially implemented' },
          { value: 'no', label: 'No EVV system' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['sandata', 'alternate'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for NJ Medicaid services.',
      atRisk: 'EVV implementation is incomplete. Ensure 100% of Medicaid visits are captured.',
      nonCompliant: 'NJ Medicaid personal care services require EVV. Claims may be denied without EVV. Contact DMAHS.',
      unknown: 'Verify EVV compliance if providing NJ Medicaid services.',
    },
    stateNotes: [
      'EVV required for Medicaid personal care per 21st Century Cures Act Section 12006',
      'NJ uses Sandata as the state EVV system per N.J.A.C. 10:60',
      'Alternate EVV vendors must be DMAHS-approved per N.J.S.A. 30:4D-6.1',
      'EVV captures location, date, time, service type, and provider/recipient info',
    ],
  },

  // ============================================
  // SECTION K: HIPAA
  // ============================================
  {
    id: 'NJ-K1',
    itemId: 'K1',
    stateCode: 'NJ',
    title: 'HIPAA Training',
    description: 'Federal requirement for HIPAA privacy and security training',
    severity: 'high',
    regulationCode: '45 CFR 164.530(b)',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff receive HIPAA training?',
        inputType: 'select',
        options: [
          { value: 'all_annual', label: 'Yes, at hire and annually' },
          { value: 'all_hire', label: 'Yes, at hire only' },
          { value: 'some', label: 'Only some staff' },
          { value: 'no', label: 'No HIPAA training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document HIPAA training completion for all staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all_annual' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'all_hire', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA training program meets federal requirements.',
      atRisk: 'HIPAA training should be provided annually. Update your training program.',
      nonCompliant: 'HIPAA training is inadequate. This violates federal law.',
      unknown: 'Review HIPAA training requirements at hhs.gov/hipaa.',
    },
    stateNotes: [
      'HIPAA training required per 45 CFR 164.530(b)',
      'Training must occur within reasonable time of hire',
      'Documentation of training completion required per N.J.A.C. 8:42-11.1',
      'Privacy policies must be consistent with N.J.S.A. 45:11-24 confidentiality requirements',
    ],
  },
];

export default NEW_JERSEY_QUESTIONS;